/* Progressive choreography. The document stays visible without this script. */
(() => {
  if (!Element.prototype.animate || !('IntersectionObserver' in window)) return;
  const reduced = window.nanaMotion || matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const ease = 'cubic-bezier(.16,1,.3,1)';
  const active = new Set();
  const seen = new WeakSet();
  const resets = [];
  let lifecycleVersion = 0;
  let readyImages = new WeakMap();
  const imageObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const pending = readyImages.get(entry.target);
      imageObserver.unobserve(entry.target);
      readyImages.delete(entry.target);
      if (!pending || pending.version !== lifecycleVersion || !entry.target.isConnected || entry.target.contains(document.activeElement)) return;
      if (!pending.image.complete || !pending.image.naturalWidth) return;
      animate(entry.target, pending.frames, pending.duration, pending.delay);
    });
  }, {threshold: .12});

  function animate(node, frames, duration = 850, delay = 0, easing = ease) {
    if (!node || reduced.matches || document.hidden) return;
    const animation = node.animate(frames, {duration, delay, easing, fill: 'backwards'});
    active.add(animation);
    animation.finished.then(() => active.delete(animation), () => active.delete(animation));
  }
  function rise(node, delay = 0, distance = 24) {
    animate(node, [{translate: `0 ${distance}px`, opacity: .35}, {translate: '0 0', opacity: 1}], 850, delay);
  }
  function title(node, delay = 0) {
    // Keep the original text nodes and localized line breaks intact.
    animate(node, [{clipPath: 'inset(0 0 100% 0)', translate: '0 24px'}, {clipPath: 'inset(-8% -4% -8% -4%)', translate: '0 0'}], 950, delay);
  }
  function animateImage(node, frames, duration, delay = 0) {
    if (!node || reduced.matches || document.hidden) return;
    const image = node.matches('img') ? node : node.querySelector('img');
    if (!image) return;
    // A slow photograph must not miss its own entrance while still loading.
    const version = lifecycleVersion;
    const ready = image.decode ? image.decode() : Promise.resolve();
    ready.catch(() => {}).then(() => {
      if (version !== lifecycleVersion || !image.complete || !image.naturalWidth || !node.isConnected || node.contains(document.activeElement)) return;
      // On mobile the section may appear before its photo. Keep the photo
      // eligible until the photo itself enters, even when decoding was fast.
      readyImages.set(node, {image, frames, duration, delay, version});
      imageObserver.observe(node);
    });
  }
  function picture(node, delay = 0) {
    animateImage(node, [{clipPath: 'inset(12% 0 12% 0)', scale: '1.06'}, {clipPath: 'inset(0)', scale: '1'}], 1150, delay);
  }
  function enter(node) {
    if (seen.has(node)) return;
    seen.add(node);
    if (reduced.matches || node.contains(document.activeElement)) return;
    if (node.matches('.nx-portal')) {
      // The original symbol stays above a short, independent light pass.
      animate(node.querySelector('.nx-surface-light'), [
        {transform: 'translate3d(-28%,7%,0)', opacity: 0, offset: 0},
        {transform: 'translate3d(-10%,2%,0)', opacity: .9, offset: .28},
        {transform: 'translate3d(18%,-3%,0)', opacity: .6, offset: .68},
        {transform: 'translate3d(38%,-8%,0)', opacity: 0, offset: 1}
      ], 4600, 0, 'cubic-bezier(.45,0,.55,1)');
      rise(node.querySelector('.nx-portal-top'), 0, 12);
      title(node.querySelector('h2'), 60);
      const one = node.querySelector('.nx-bottle-one');
      const two = node.querySelector('.nx-bottle-two');
      animateImage(one, [{translate: '-65px 100px', rotate: '-13deg', scale: '.84', opacity: .25}, {translate: '0 0', rotate: '0deg', scale: '1', opacity: 1}], 1350, 50);
      animateImage(two, [{translate: '55px 120px', rotate: '15deg', scale: '.88', opacity: .25}, {translate: '0 0', rotate: '0deg', scale: '1', opacity: 1}], 1350, 140);
      animate(node.querySelector('.nx-scene-orbit'), [{scale: '.65', opacity: 0}, {scale: '1', opacity: 1}], 1300, 70);
      picture(node.querySelector('.nx-clinic-window'), 100);
      // The CTA remains a live link throughout the intro.
      rise(node.querySelector('.nx-portal-bottom'), 200, 18);
    } else if (node.matches('.nx-shop-intro')) {
      title(node.querySelector('h1'));
      if (!node.querySelector('.nx-shop-duet')) picture(node.querySelector('.nx-shop-hero-picture'), 70);
      rise(node.querySelector(':scope>div>p'), 90);
    } else if (node.matches('.n-hero')) {
      title(node.querySelector('h1'));
      picture(node.querySelector('.n-hero-photo'), 90);
      rise(node.querySelector('.n-hero-description'), 120);
    } else if (node.matches('.nx-edit-art')) {
      picture(node, 0);
    } else if (node.matches('.nx-gallery-stage')) {
      animateImage(node, [{clipPath: 'inset(28% 0 28% 0)', scale: '.96'}, {clipPath: 'inset(0)', scale: '1'}], 1050);
    } else if (node.matches('.nx-detail-copy')) {
      rise(node.querySelector('.nx-kicker'), 0, 12);
      title(node.querySelector('h1'), 70);
      rise(node.querySelector('.nx-detail-price'), 120, 16);
    } else if (node.matches('.nx-contact-row,.n-treatment-list>a')) {
      // Each row enters on its own on a phone; links remain immediately usable.
      animate(node, [{translate: '0 22px', opacity: .35}, {translate: '0 0', opacity: 1}], 650);
    } else if (node.matches('.nx-product-description>h2')) {
      title(node);
    } else {
      title(node.querySelector('h2'));
      rise(node.querySelector(':scope>.nx-kicker,:scope>.nx-outline-link'), 100, 16);
      const photo = node.querySelector('.n-about-photo, .n-wellness>img');
      if (photo && location.hash !== '#n-about') picture(photo, 60);
      if (node.matches('.n-photographic-strip')) {
        node.querySelectorAll('img').forEach((img, index) => picture(img, index * 80));
      }
    }
  }

  // The two entrance panels trigger independently on a phone.
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      enter(entry.target);
      observer.unobserve(entry.target);
    });
  }, {threshold: .12});
  document.querySelectorAll([
    '.nx-portal', '.nx-shop-intro', '.n-hero', '.n-about', '.n-services-heading', '.n-photographic-strip', '.n-wellness',
    '.nx-gallery-stage', '.nx-detail-copy', '.nx-product-description>h2', '.nx-edit-heading', '.nx-edit-art',
    '.nx-contact-section-title', '.nx-contact-row', '.nx-contact-closing',
    '.nx-other-side', '.nx-shop-qr>div', '.n-footer-top', '.n-treatment-list>a'
  ].join(',')).forEach(node => observer.observe(node));

  // No idle requestAnimationFrame loop, and only one layout read per pointer entry.
  document.querySelectorAll('.nx-portal').forEach(portal => {
    let box = null;
    let frame = 0;
    const reset = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      box = null;
      portal.style.removeProperty('--nx-pointer-x');
      portal.style.removeProperty('--nx-pointer-y');
    };
    resets.push(reset);
    portal.addEventListener('pointerenter', event => {
      if (event.pointerType === 'mouse' && fine.matches && !reduced.matches) {
        portal.setAttribute('data-nx-depth', '');
        box = portal.getBoundingClientRect();
      }
    });
    portal.addEventListener('pointermove', event => {
      if (!box || reduced.matches || !fine.matches || document.hidden || event.pointerType !== 'mouse') return;
      const x = Math.max(-1, Math.min(1, (event.clientX - box.left) / box.width * 2 - 1)) * 16;
      const y = Math.max(-1, Math.min(1, (event.clientY - box.top) / box.height * 2 - 1)) * 12;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        portal.style.setProperty('--nx-pointer-x', `${x.toFixed(2)}px`);
        portal.style.setProperty('--nx-pointer-y', `${y.toFixed(2)}px`);
        frame = 0;
      });
    });
    portal.addEventListener('pointerleave', reset);
    portal.addEventListener('pointercancel', reset);
    portal.addEventListener('focusin', reset);
  });
  const resetPointers = () => resets.forEach(reset => reset());
  const settle = () => {
    lifecycleVersion += 1;
    imageObserver.disconnect();
    readyImages = new WeakMap();
    active.forEach(animation => animation.cancel());
    active.clear();
    resetPointers();
  };
  addEventListener('resize', resetPointers, {passive: true});
  addEventListener('scroll', resetPointers, {passive: true});
  addEventListener('pagehide', settle);
  document.addEventListener('visibilitychange', () => { if (document.hidden) settle(); });
  reduced.addEventListener('change', () => { if (reduced.matches) settle(); });
  fine.addEventListener('change', resetPointers);
})();
